'use client'

import {
    getOrphansForAppointment,
    linkOrphanPayment,
    OrphanAppointmentType,
    OrphanPayment,
} from '@/lib/api/financial/orphan-payments-api'
import { notifyOrphanPaymentsChanged } from '@/lib/hooks/use-orphan-payments-count'
import { createRef, useEffect, useState } from 'react'
import { MdArrowBack, MdInfoOutline, MdPix, MdReceiptLong } from 'react-icons/md'
import { toast } from 'react-toastify'

interface GenerateBillingModalProps {
    isOpen: boolean
    onClose: () => void
    /** Valor em CENTAVOS. `undefined` = cobrar o saldo inteiro. */
    onConfirm: (amount?: number) => void
    /** Quanto ainda pode ser cobrado agora, em centavos. */
    chargeable: number
    /**
     * O valor pode ser menor que o saldo?
     *
     * `false` na PRIMEIRA cobrança de exame e consulta: quem segura o repasse até o
     * cliente fechar a conta só existe para procedimento, e uma primeira cobrança
     * parcial pagaria o parceiro pelo valor cheio (ou nunca pagaria). Dividir
     * continua possível pela cobrança complementar.
     */
    allowPartial: boolean
    /** "procedimento", "exame", "consulta" — usado nas frases. */
    entityLabel: string
    loading?: boolean
    /**
     * O atendimento. Presente, o modal abre perguntando se o paciente JÁ PAGOU por Pix
     * direto na chave: era aqui que o dinheiro duplicava — o Pix entrava solto e a
     * cobrança nova era marcada como paga em "Outro" (agosto/2026: R$ 7.300 de uma só
     * paciente). Ver `billing-system.md` §19.
     */
    appointment?: { type: OrphanAppointmentType; id: number }
    /** Chamado depois de vincular um Pix já recebido (a tela recarrega o atendimento). */
    onOrphanLinked?: () => void
}

type Step = 'choose' | 'pix' | 'amount'

const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso)) : '-'

const money = (centavos: number) =>
    (centavos / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/**
 * Valor da cobrança, perguntado na CRIAÇÃO.
 *
 * O valor de uma cobrança atrelada a atendimento não pode ser alterado depois — é
 * por ali que sairia desconto sem autorização. Como cobrar em partes é legítimo (e
 * não é desconto: o saldo continua devido), o número é digitado aqui, no gesto de
 * criar, onde o backend valida contra o saldo em aberto.
 */
export default function GenerateBillingModal({
    isOpen,
    onClose,
    onConfirm,
    chargeable,
    allowPartial,
    entityLabel,
    loading = false,
    appointment,
    onOrphanLinked,
}: GenerateBillingModalProps) {
    const modalRef = createRef<HTMLDialogElement>()
    const [value, setValue] = useState('')
    const [step, setStep] = useState<Step>('amount')
    const [orphans, setOrphans] = useState<Array<OrphanPayment & { exact: boolean }>>([])
    const [orphansLoading, setOrphansLoading] = useState(false)
    const [orphansError, setOrphansError] = useState<string | null>(null)
    const [selectedOrphan, setSelectedOrphan] = useState<number | null>(null)
    const [linking, setLinking] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setValue((chargeable / 100).toFixed(2))
            setStep(appointment ? 'choose' : 'amount')
            setSelectedOrphan(null)
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
        }
    }, [isOpen, chargeable])

    const openPixStep = async () => {
        if (!appointment) return
        setStep('pix')
        setOrphansLoading(true)
        setOrphansError(null)
        try {
            const response = await getOrphansForAppointment(appointment.type, appointment.id, chargeable)
            if (response.success) {
                const list = response.result ?? []
                setOrphans(list)
                setSelectedOrphan(list.find(o => o.exact)?.id ?? null)
            } else {
                setOrphans([])
                setOrphansError(response.error || 'Não foi possível buscar os Pix recebidos.')
            }
        } finally {
            setOrphansLoading(false)
        }
    }

    const linkSelected = async () => {
        if (!appointment || selectedOrphan == null) return
        setLinking(true)
        try {
            const response = await linkOrphanPayment(selectedOrphan, appointment.type, appointment.id)
            if (response.success) {
                toast.success(response.result?.message || 'Pix vinculado ao atendimento.')
                notifyOrphanPaymentsChanged()
                onOrphanLinked?.()
                onClose()
            } else {
                toast.error(response.error || 'Não foi possível vincular o Pix.')
            }
        } finally {
            setLinking(false)
        }
    }

    const centavos = Math.round((parseFloat(value.replace(',', '.')) || 0) * 100)
    const invalid = centavos <= 0 || centavos > chargeable
    const isPartial = centavos > 0 && centavos < chargeable

    return (
        <dialog ref={modalRef} className="modal" onClose={onClose}>
            <div className="modal-box max-w-md !rounded-2xl">
                <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <MdReceiptLong size={22} />
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold">Gerar cobrança</h3>
                        <p className="mt-1 text-sm text-base-content/70">
                            Saldo em aberto do {entityLabel}:{' '}
                            <strong>{money(chargeable)}</strong>
                        </p>
                    </div>
                </div>

                {step === 'choose' && (
                    <div className="mt-5 flex flex-col gap-3">
                        <p className="text-sm font-medium">O paciente já pagou?</p>
                        <button
                            type="button"
                            className="text-left rounded-xl border border-base-300 hover:border-secondary hover:bg-secondary/5 p-4 flex gap-3 transition-colors"
                            onClick={openPixStep}
                        >
                            <MdPix size={22} className="shrink-0 text-secondary mt-0.5" />
                            <span>
                                <span className="block font-medium">Já foi pago por chave Pix fora do sistema</span>
                                <span className="block text-xs text-base-content/60 mt-0.5">
                                    Mostra os Pix recebidos sem vínculo para ligar a este {entityLabel}. Nenhuma cobrança nova é criada.
                                </span>
                            </span>
                        </button>
                        <button
                            type="button"
                            className="text-left rounded-xl border border-base-300 hover:border-primary hover:bg-primary/5 p-4 flex gap-3 transition-colors"
                            onClick={() => setStep('amount')}
                        >
                            <MdReceiptLong size={22} className="shrink-0 text-primary mt-0.5" />
                            <span>
                                <span className="block font-medium">Não foi pago, gerar nova cobrança</span>
                                <span className="block text-xs text-base-content/60 mt-0.5">
                                    Cria a cobrança para o paciente pagar.
                                </span>
                            </span>
                        </button>
                    </div>
                )}

                {step === 'pix' && (
                    <div className="mt-5 flex flex-col gap-3">
                        <button type="button" className="btn btn-ghost btn-xs w-fit gap-1 -ml-2" onClick={() => setStep('choose')}>
                            <MdArrowBack /> Voltar
                        </button>
                        {orphansLoading ? (
                            <div className="flex justify-center py-6">
                                <span className="loading loading-spinner loading-md text-secondary" />
                            </div>
                        ) : orphansError ? (
                            <p className="text-sm text-error">{orphansError}</p>
                        ) : !orphans.length ? (
                            <div className="rounded-xl border border-info/30 bg-info/10 p-3 text-sm text-base-content/80 flex gap-2">
                                <MdInfoOutline size={18} className="mt-0.5 shrink-0 text-info" />
                                <span>
                                    Nenhum Pix sem vínculo nos últimos 60 dias cabe neste valor. Se o Pix entrou agora, ele pode
                                    levar alguns minutos para aparecer — confira o extrato. Não registre o pagamento à mão antes
                                    de encontrá-lo.
                                </span>
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-base-content/70">
                                    Escolha o Pix que pagou este {entityLabel}. Quem pagou pode ser outra pessoa (o marido, a empresa):
                                    confira pelo valor e pela data.
                                </p>
                                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                                    {orphans.map(o => (
                                        <label
                                            key={o.id}
                                            className={`rounded-xl border p-3 flex items-center gap-3 cursor-pointer ${
                                                selectedOrphan === o.id ? 'border-secondary bg-secondary/5' : 'border-base-300'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                className="radio radio-secondary radio-sm"
                                                checked={selectedOrphan === o.id}
                                                onChange={() => setSelectedOrphan(o.id)}
                                            />
                                            <span className="flex-1 min-w-0">
                                                <span className="block font-medium">
                                                    {money(o.amount)}
                                                    {o.exact && <span className="badge badge-success badge-sm text-white ml-2">mesmo valor</span>}
                                                </span>
                                                <span className="block text-xs text-base-content/60 truncate">
                                                    {formatDate(o.paidAt)} · {o.payerName || 'pagador ainda não identificado'}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {step === 'amount' && (
                <div className="mt-5">
                    <label className="label pb-1" htmlFor="generate-billing-amount">
                        <span className="label-text font-medium">
                            Valor a cobrar agora
                        </span>
                    </label>
                    <label className="input input-bordered flex items-center gap-2 !rounded-lg">
                        <span className="text-base-content/60">R$</span>
                        <input
                            id="generate-billing-amount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={(chargeable / 100).toFixed(2)}
                            className="grow"
                            value={value}
                            disabled={!allowPartial || loading}
                            onChange={e => setValue(e.target.value)}
                        />
                    </label>

                    {allowPartial ? (
                        <p className="mt-2 text-xs text-base-content/60">
                            Cobrando menos que o saldo, o restante continua devido
                            e pode ser cobrado depois — não é desconto.
                        </p>
                    ) : (
                        <div className="mt-3 flex gap-2 rounded-xl border border-info/30 bg-info/10 p-3 text-sm">
                            <MdInfoOutline
                                size={18}
                                className="mt-0.5 shrink-0 text-info"
                            />
                            <p className="text-base-content/80">
                                A primeira cobrança deste {entityLabel} precisa
                                cobrir o valor total. Para dividir o pagamento,
                                gere esta e depois uma cobrança complementar.
                            </p>
                        </div>
                    )}

                    {allowPartial && isPartial && (
                        <p className="mt-2 text-sm text-warning">
                            Ficará um saldo de {money(chargeable - centavos)} a
                            cobrar.
                        </p>
                    )}
                    {allowPartial && centavos > chargeable && (
                        <p className="mt-2 text-sm text-error">
                            O valor não pode passar do saldo em aberto.
                        </p>
                    )}
                </div>

                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost !rounded-lg"
                        onClick={onClose}
                        disabled={loading || linking}
                    >
                        Cancelar
                    </button>
                    {step === 'pix' && orphans.length > 0 && (
                        <button
                            type="button"
                            className="btn btn-secondary !rounded-lg"
                            onClick={linkSelected}
                            disabled={linking || selectedOrphan == null}
                        >
                            {linking && <span className="loading loading-spinner loading-xs" />}
                            Vincular este Pix
                        </button>
                    )}
                    {step === 'amount' && (
                    <button
                        type="button"
                        className="btn btn-primary !border-0 !rounded-lg"
                        onClick={() =>
                            onConfirm(
                                allowPartial && centavos !== chargeable
                                    ? centavos
                                    : undefined,
                            )
                        }
                        disabled={loading || (allowPartial && invalid)}
                    >
                        {loading && (
                            <span className="loading loading-spinner loading-xs" />
                        )}
                        Gerar cobrança
                    </button>
                    )}
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
